import { EditorV3, IEditorV3, stringToV3 } from "@azu-business/editor-v3";
import { stylesToV3 } from "components/common/stylesToV3";
import { useAppSettings, useHierarchyContext, useTlfContext } from "contexts/AppSettingsContext";
import { UPDATE_TLF } from "contexts/tlf/interface";
import { eHierarchyLevel } from "functions/api/enums";
import { eTlfType } from "functions/api/styles/getAllStyles";
import { TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { Col, Container, Row } from "react-bootstrap";

/**
 * Editor for standards notes attached to a TLF item with access controlled by hierarchy.
 *
 * @returns A JSX element rendering the standards notes editor or an empty fragment.
 */
export const StandardsNotes = (): React.ReactNode => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const tlfContext = useTlfContext();
  const tlfData: TlfItemReadDto | null =
    tlfContext && tlfContext.tlfData && "standardsNotes" in tlfContext.tlfData
      ? (tlfContext.tlfData as TlfItemReadDto)
      : null;

  const standardNotes: IEditorV3 =
    (tlfContext.tlfData as TlfItemReadDto)?.standardsNotes ??
    // istanbul ignore next
    stringToV3("");

  return !hierarchyContext || !hierarchyContext.hierarchy || !tlfContext || !tlfData ? null : (
    <Container>
      <Row>
        <Col>
          <h4>Standards notes</h4>
          <div className="notes-box">
            <EditorV3
              id="standardsnotes"
              allowNewLine
              customStyleMap={stylesToV3(appSettings.styles, eTlfType.Template)}
              debounceMilliseconds={-1}
              editable={
                !tlfContext.readOnly &&
                [eHierarchyLevel.Corporate, eHierarchyLevel.TA, eHierarchyLevel.Project].includes(
                  hierarchyContext.hierarchy.level,
                )
              }
              input={standardNotes}
              setObject={(ret) => {
                tlfContext.dispatch({
                  operation: UPDATE_TLF,
                  tlfData: { standardsNotes: ret },
                  calledFrom: "StandardsNotes:setValue",
                });
              }}
              style={{
                ...appSettings.defaultTableStyle,
                minHeight: "120px",
                height: "120px",
                border: "none",
                backgroundColor:
                  !tlfContext.readOnly &&
                  [eHierarchyLevel.Corporate, eHierarchyLevel.TA, eHierarchyLevel.Project].includes(
                    hierarchyContext?.hierarchy?.level,
                  )
                    ? undefined
                    : "rgba(211, 211, 211, 0.16)",
              }}
            />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

StandardsNotes.displayName = "StandardsNotes";
